import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { assets } from '../../assets/assets';
import { AppContext } from '../../contexts/AppContext';
import { isInterSiteProspectionEnabled, isTerrainProspectionEnabled } from '../../utils/fieldVisitAccess';

const EmployeeMobileNav = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useContext(AppContext);

  const showTerrainNav = isTerrainProspectionEnabled(userData?.entreprise);
  const showInterSiteNav = isInterSiteProspectionEnabled(userData?.entreprise);
  const showFieldVisitNav = showTerrainNav || showInterSiteNav;

  const isActive = (path) => {
    if (path === '/employeeDashboard' || path === '/dashboard' || path === '/home') {
      return location.pathname === '/dashboard' || location.pathname === '/home' || location.pathname === '/employeeDashboard';
    }
    if (path === '/pointage-terrain') {
      return location.pathname.startsWith('/pointage-terrain');
    }
    if (path === '/pointage-inter-site') {
      return location.pathname.startsWith('/pointage-inter-site');
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="fixed -bottom-2 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-[9999]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}>
      <div className="flex justify-around items-center py-2 px-4">
        {/* Accueil */}
        <div 
          onClick={() => navigate('/employeeDashboard')}
          className={`flex flex-col items-center justify-center cursor-pointer hover:opacity-70 transition-opacity ${isActive('/employeeDashboard') ? 'opacity-100' : 'opacity-70'}`}
        >
          <img 
            src={assets.home} 
            alt="Home" 
            className="w-8 h-8 object-contain"
          />
          <span className={`text-xs font-medium -mt-1 ${isActive('/employeeDashboard') ? 'text-blue-600' : 'text-gray-700'}`}>
            Accueil
          </span>
        </div>
        
        {/* Pointage terrain / inter-site (masqué si aucun module activé pour l’entreprise) */}
        {showFieldVisitNav ? (
          showTerrainNav && showInterSiteNav ? (
            <div className="flex items-stretch justify-center gap-1 min-w-0 max-w-[5.5rem]">
              <div
                onClick={() => navigate('/pointage-terrain')}
                className={`flex flex-1 min-w-0 flex-col items-center justify-center cursor-pointer hover:opacity-70 transition-opacity ${isActive('/pointage-terrain') ? 'opacity-100' : 'opacity-70'}`}
              >
                <div className="relative flex h-[2.125rem] w-[2.125rem] shrink-0 items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-2 border-[#0026E2] bg-white" />
                  <img
                    src={assets.loca}
                    alt=""
                    className="relative h-[1.55rem] w-[1.55rem] object-contain"
                  />
                </div>
                <span
                  className={`text-[10px] font-medium -mt-0.5 text-center leading-tight px-0.5 ${isActive('/pointage-terrain') ? 'text-blue-600' : 'text-gray-700'}`}
                >
                  {t('fieldVisit.navShort')}
                </span>
              </div>
              <div
                onClick={() => navigate('/pointage-inter-site')}
                className={`flex flex-1 min-w-0 flex-col items-center justify-center cursor-pointer hover:opacity-70 transition-opacity ${isActive('/pointage-inter-site') ? 'opacity-100' : 'opacity-70'}`}
              >
                <div className="relative flex h-[2.125rem] w-[2.125rem] shrink-0 items-center justify-center">
                  <div className="absolute inset-0 rounded-full border-2 border-[#0026E2] bg-white" />
                  <img
                    src={assets.pointageInterSite}
                    alt=""
                    className="relative h-[1.55rem] w-[1.55rem] object-contain opacity-90"
                  />
                </div>
                <span
                  className={`text-[10px] font-medium -mt-0.5 text-center leading-tight px-0.5 ${isActive('/pointage-inter-site') ? 'text-blue-600' : 'text-gray-700'}`}
                >
                  {t('fieldVisit.navShortInterSite')}
                </span>
              </div>
            </div>
          ) : showInterSiteNav ? (
            <div
              onClick={() => navigate('/pointage-inter-site')}
              className={`flex flex-col items-center justify-center cursor-pointer hover:opacity-70 transition-opacity ${isActive('/pointage-inter-site') ? 'opacity-100' : 'opacity-70'}`}
            >
              <div className="relative flex h-[2.125rem] w-[2.125rem] shrink-0 items-center justify-center">
                <div className="absolute inset-0 rounded-full border-2 border-[#0026E2] bg-white" />
                <img
                  src={assets.pointageInterSite}
                  alt=""
                  className="relative h-[1.55rem] w-[1.55rem] object-contain"
                />
              </div>
              <span
                className={`text-xs font-medium -mt-1 ${isActive('/pointage-inter-site') ? 'text-blue-600' : 'text-gray-700'}`}
              >
                {t('fieldVisit.navShortInterSite')}
              </span>
            </div>
          ) : (
            <div
              onClick={() => navigate('/pointage-terrain')}
              className={`flex flex-col items-center justify-center cursor-pointer hover:opacity-70 transition-opacity ${isActive('/pointage-terrain') ? 'opacity-100' : 'opacity-70'}`}
            >
              <div className="relative flex h-[2.125rem] w-[2.125rem] shrink-0 items-center justify-center">
                <div className="absolute inset-0 rounded-full border-2 border-[#0026E2] bg-white" />
                <img
                  src={assets.loca}
                  alt=""
                  className="relative h-[1.55rem] w-[1.55rem] object-contain"
                />
              </div>
              <span className={`text-xs font-medium -mt-1 ${isActive('/pointage-terrain') ? 'text-blue-600' : 'text-gray-700'}`}>
                {t('fieldVisit.navShort')}
              </span>
            </div>
          )
        ) : null}

        {/* Projets */}
        <div 
          onClick={() => navigate('/projets')}
          className={`flex flex-col items-center justify-center cursor-pointer hover:opacity-70 transition-opacity ${isActive('/projets') ? 'opacity-100' : 'opacity-70'}`}
        >
          <img 
            src={assets.cartes} 
            alt="Projets" 
            className="w-8 h-8 object-contain"
          />
          <span className={`text-xs font-medium -mt-1 ${isActive('/projets') ? 'text-blue-600' : 'text-gray-700'}`}>
            Projets
          </span>
        </div>
        
        {/* Paie */}
        <div
          onClick={() => navigate('/mes-bulletins')}
          className={`flex flex-col items-center justify-center cursor-pointer hover:opacity-70 transition-opacity ${isActive('/mes-bulletins') ? 'opacity-100' : 'opacity-70'}`}
        >
          <img
            src={assets.rapport}
            alt="Paie"
            className="w-8 h-8 object-contain"
          />
          <span className={`text-xs font-medium -mt-1 ${isActive('/mes-bulletins') ? 'text-blue-600' : 'text-gray-700'}`}>
            Paie
          </span>
        </div>

        {/* Horaires */}
        <div 
          onClick={() => navigate('/horaire')}
          className={`flex flex-col items-center justify-center cursor-pointer hover:opacity-70 transition-opacity ${isActive('/horaire') ? 'opacity-100' : 'opacity-70'}`}
        >
          <img 
            src={assets.retards} 
            alt="Horaires" 
            className="w-8 h-8 object-contain"
          />
          <span className={`text-xs font-medium -mt-1 ${isActive('/horaire') ? 'text-blue-600' : 'text-gray-700'}`}>
            Horaires
          </span>
        </div>
        
        {/* Profil */}
        <div 
          onClick={() => navigate('/profil')}
          className={`flex flex-col items-center justify-center cursor-pointer hover:opacity-70 transition-opacity ${isActive('/profil') ? 'opacity-100' : 'opacity-70'}`}
        >
          <img 
            src={assets.employes} 
            alt="Profil" 
            className="w-8 h-8 object-contain"
          />
          <span className={`text-xs font-medium -mt-1 ${isActive('/profil') ? 'text-blue-600' : 'text-gray-700'}`}>
            Profil
          </span>
        </div>
      </div>
    </div>
  );
};

export default EmployeeMobileNav;
